import {Component, computed, input, OnInit, output, signal, inject} from '@angular/core';
import moment from 'moment';
import {Moment} from 'moment';
import {DayOfWeek, dayOfWeekValues} from '../../../classes/enums/DayOfWeek';
import {Availability} from '../../../classes/models/employee/Availability';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
  AvailabilityEditCalendarDayComponent
} from './availability-edit-calendar-day/availability-edit-calendar-day.component';
import {EditType} from '../../../classes/enums/EditType';
import {DataService} from '../../../services/store/data.service';
import {WeekTimeSlotsService} from '../../../services/week-time-slot.service';
import {StoreWorkingTime} from '../../../classes/models/store/StoreWorkingTime';
import {WorkingHoursService} from '../../../services/store/working-hours.service';
import {StoreDetails} from '../../../classes/models/store/StoreDetails';
import {AvailabilityDeleteWeekComponent} from './availability-delete-week/availability-delete-week.component';
import {EmployeePositionId} from '../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';
import {WeekSelectComponent} from "../../../shared/components/week-select/week-select.component";

/**
 * This component handles showing and editing availabilities.
 */
@Component({
  selector: 'app-availability',
  templateUrl: './availability.component.html',
  styleUrls: ['./availability.component.less'],
  imports: [SHARED_IMPORTS, WeekSelectComponent]
})
export class AvailabilityComponent implements OnInit {
  private modalService = inject(NgbModal);
  private weekTimeSlotsService = inject(WeekTimeSlotsService);
  private workingHoursService = inject(WorkingHoursService);
  dataService = inject(DataService);

  /** Identifiers of an employee and position. */
  employeePositionId = input<EmployeePositionId>();
  /** Detailed information about a store, assigned to an employee. */
  storeDetails = input<StoreDetails>();
  /** A map from start date to a list of availabilities for one week. */
  data = input<Map<string, Availability[]>>();
  /** An event to refresh employee page data. */
  getEmployeePageDataEvent = output();

  /** A list of all days of the week. */
  weekdays: DayOfWeek[] = dayOfWeekValues();
  /** The height of one cell in the grid of availabilities. */
  cellHeight = 40;
  /** The Monday of the selected week. */
  selectedMonday = signal<string>(undefined);
  /** The Monday of the last possible week possible to select. */
  lastVisibleMonday = signal<string>(undefined);
  /** The first date from which availability data is allowed to be edited. */
  editDate = signal<string>(undefined);
  /** Assigns user edit action type, so it can be used in HTML. */
  EditType = EditType;

  /** A map of availabilities for the selected week. Recomputes when the selected week or input data changes. */
  weekAvailabilities = computed(() => {
    const monday = this.selectedMonday();
    const data = this.data();
    if (!monday || !data) {
      return new Map<DayOfWeek, Availability>();
    }
    return this.weekTimeSlotsService.getWeekData(monday, data);
  });

  /**
   * A list of store opening times (with extra time added before opening a store) for the selected week, with special working times
   * taken into account.
   */
  storeHoursFrom = computed<Moment[]>(() => {
    const details = this.storeDetails();
    const monday = this.selectedMonday();
    if (!details || !monday) return [];
    return this.workingHoursService.getStoreOpeningTimesStartingAt(monday, details);
  });

  /**
   * A list of store closing times (with extra time added after closing a store) for the selected week, with special working times
   * taken into account.
   */
  storeHoursTo = computed<Moment[]>(() => {
    const details = this.storeDetails();
    const monday = this.selectedMonday();
    if (!details || !monday) return [];
    return this.workingHoursService.getStoreClosingTimesStartingAt(monday, details);
  });

  /** A list of hours, formatted in HH:00:00, which are between shop opening and closing hours. */
  hours = computed<string[]>(() => {
    const details = this.storeDetails();
    const weekAvails = this.weekAvailabilities();
    const hoursFrom = this.storeHoursFrom();
    const hoursTo = this.storeHoursTo();
    if (!details || hoursFrom.length === 0) return [];

    const availabilitiesFrom = Array.from(weekAvails.values())
            .map(value => moment(value.startTime, 'HH:mm:ss'));
    const fromHour = availabilitiesFrom.concat(hoursFrom)
            .reduce((prev, curr) => prev.isBefore(curr) ? prev : curr, moment('23', 'HH'));

    const availabilitiesTo = Array.from(weekAvails.values())
            .map(value => moment(value.endTime, 'HH:mm:ss'));
    const toHour = availabilitiesTo.concat(hoursTo)
            .reduce((prev, curr) => prev.isAfter(curr) ? prev : curr, moment('01', 'HH'));

    return this.workingHoursService.calculateHoursRange(fromHour, toHour);
  });

  /** Checks if a week can be edited on the selected week. */
  canEdit = computed(() => this.dataService.canEditAtDate(this.selectedMonday()));

  /**
   * Selects the current week on page initialization. Sets up data, which can be got from {@link DataService}.
   */
  ngOnInit(): void {
    if (this.dataService.selectedStore) {
      const currentWeekMonday = moment().startOf('week').isoWeekday(1).format('YYYY-MM-DD');
      this.selectedMonday.set(currentWeekMonday);
      this.editDate.set(this.dataService.editDate);
      this.lastVisibleMonday.set(
              moment(this.dataService.rangeTo, 'YYYY-MM-DD')
                      .subtract(6, 'day')
                      .format('YYYY-MM-DD')
      );
    }
  }

  /**
   * Updates all data, related to selecting a specific week.
   * @param weekStart the Monday from which the selected week starts.
   */
  selectWeek(weekStart: string): void {
    this.selectedMonday.set(weekStart);
  }

  /**
   * Gets start time from which availability's HTML <div> element will be displayed. This method ensures that start time will not
   * be displayed earlier than store opening time because the grid starts from store opening time.
   * @param availability an availability which is displayed.
   */
  getStartTimeForCalendar(availability: Availability): string {
    const h = this.hours();
    return availability.startTime < h[0] ? h[0] : availability.startTime;
  }

  /**
   * Gets end time until which availability's HTML <div> element will be displayed. This method ensures that end time will not
   * be displayed later than store closing time because the grid ends at store ending time.
   * @param availability an availability which is displayed.
   */
  getEndTimeForCalendar(availability: Availability): string {
    const h = this.hours();
    const gridEnd = h[h.length - 1];
    return availability.endTime > gridEnd ? gridEnd : availability.endTime;
  }

  /**
   * Opens a modal window to update an availability in a week.
   * @param dayOfWeek the day of week of an availability, a user currently editing.
   * @param editType an edit action type, which is performed by a user.
   */
  editCalendarDay(dayOfWeek: DayOfWeek, editType: EditType) {
    const modalRef = AvailabilityEditCalendarDayComponent.open(this.modalService, {
      employeePositionId: this.employeePositionId(),
      startDate: this.selectedMonday(),
      dayOfWeek,
      availabilities: this.weekAvailabilities(),
      storeWorkingTime: this.getWorkingTimes(dayOfWeek),
      weekExists: !!this.data().get(this.selectedMonday()),
      editType
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  /**
   * Opens a modal window to delete the whole week of availabilities.
   */
  deleteWeek() {
    const modalRef = AvailabilityDeleteWeekComponent.open(this.modalService, {
      startDate: this.selectedMonday(),
      employeePositionId: this.employeePositionId(),
    });
    modalRef.result.then(() => {
      this.getEmployeePageDataEvent.emit();
    });
  }

  private getWorkingTimes(dayOfWeek: DayOfWeek): StoreWorkingTime {
    const index = dayOfWeekValues().indexOf(dayOfWeek);
    return new StoreWorkingTime(
            this.storeHoursFrom()[index].format('HH:mm:ss'),
            this.storeHoursTo()[index].format('HH:mm:ss')
    );
  }
}